import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector, useStore } from "react-redux";

import { Bundle, Observation } from "@medplum/fhirtypes";
import { useMedplum, useSubscription } from "@medplum/react";

import * as Sentry from "@sentry/react";

import { CreateEncounterContext } from "@/contexts/create-encounter";
import { useRelatedPatientPostpartum } from "@/encounters/components/EncounterLabor/hooks/useRelatedPatientPostpartum";
import { fetchLaborStageObservations } from "@/encounters/components/EncounterLabor/services/labor";
import { LaborStage } from "@/encounters/components/EncounterLabor/types/labor";
import { dedupeLaborStagesPreservingAmendPreviews } from "@/encounters/components/EncounterLabor/utils/labor-amend-preview-labor-stages";
import {
  applyMotherCumulativeBloodLossIfNewer,
  isMotherCumulativeBloodLossObservation,
  observationMetaTimeMs,
  runLaborEncounterObservationBundle,
} from "@/encounters/components/EncounterLabor/utils/labor-encounter-live-observation-bundle";
import {
  filterObservationsForPostpartumRedux,
  mergeIncomingObservationsIntoCache,
} from "@/encounters/components/EncounterLabor/utils/postpartum-observation-processing";
import { MEDPLUM_OBSERVATION_SEARCH_MAX_COUNT } from "@/encounters/constants/medplum-search";
import { RootState } from "@/store";
import { selectBirthDateTime, selectIsOptimisticBirth } from "@/store/slices/birth";
import { loadLaborStages } from "@/store/slices/labor-stages";
import {
  clearPostpartumObservations,
  loadPostpartumObservations,
  selectPostpartumObservations,
} from "@/store/slices/postpartum-observations";
import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";
import { toast } from "@/utils/notifications";

export type LaborEncounterLiveObservationsContextValue = {
  encounterId: string;
  clientId: string;
  refreshObservations: () => Promise<void>;
  /** Latest cumulative blood loss (LOINC) for the charted client on this encounter; drives CumulativeBloodLossField without a second subscription */
  liveCumulativeBloodLoss: number | undefined;
};

export const LaborEncounterLiveObservationsContext =
  createContext<LaborEncounterLiveObservationsContextValue | null>(null);

export function LaborEncounterLiveObservationsProvider({
  encounterId,
  clientId,
  children,
  skipInitialRefresh = false,
}: {
  encounterId: string;
  clientId: string;
  children: React.ReactNode;
  /** When true, defers the mount-time observation refresh until this becomes false. */
  skipInitialRefresh?: boolean;
}) {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const medplum = useMedplum();
  const { statusChanged } = useContext(CreateEncounterContext);
  const previousStatusChangedRef = useRef(statusChanged);
  const birthDateTime = useSelector(selectBirthDateTime);
  const isOptimistic = useSelector(selectIsOptimisticBirth);
  const stages = useSelector(
    (state: RootState) => state.laborStages.entities as Record<LaborStage, Observation>,
  );

  const [liveCumulativeBloodLoss, setLiveCumulativeBloodLoss] = useState<number | undefined>(
    undefined,
  );

  const observationsCacheRef = useRef<Map<string, Observation>>(new Map());
  /** Invalidates in-flight async work when a newer subscription bundle starts. */
  const bundleAsyncSeqRef = useRef(0);
  /** Invalidates overlapping HTTP refresh runs (e.g. reconnect vs birthDateTime). */
  const refreshAsyncSeqRef = useRef(0);
  /** Drop out-of-order CBL websocket updates (same field, older meta). */
  const motherCblLastAppliedMetaMsRef = useRef(-1);
  /** Skip first `onSubscriptionConnect` (initial mount already runs `refreshObservations` via effect). */
  const subscriptionConnectHandledOnceRef = useRef(false);
  /**
   * Serialize subscription bundle handling so two websocket callbacks cannot interleave:
   * that would advance `bundleAsyncSeqRef` mid-await and drop postpartum / labor-stage work.
   */
  const bundleProcessingChainRef = useRef(Promise.resolve());

  const { relatedPatientId, fetchRelatedPatientObservations } = useRelatedPatientPostpartum({
    medplum,
    encounterId,
    clientId,
    isOptimistic,
  });

  const processObservations = useCallback(
    async (observations: Observation[]) => {
      const filtered = filterObservationsForPostpartumRedux(observations, birthDateTime);
      dispatch(loadPostpartumObservations(filtered));
    },
    [dispatch, birthDateTime],
  );

  const processObservationsRef = useRef<(observations: Observation[]) => Promise<void>>();
  processObservationsRef.current = processObservations;
  const stagesRef = useRef(stages);
  const relatedPatientIdRef = useRef<string | null>(relatedPatientId);

  useEffect(() => {
    stagesRef.current = stages;
  }, [stages]);

  useEffect(() => {
    relatedPatientIdRef.current = relatedPatientId;
  }, [relatedPatientId]);

  const refreshObservations = useCallback(async () => {
    if (!encounterId || !clientId) {
      return;
    }
    const seq = ++refreshAsyncSeqRef.current;
    try {
      const result = await medplum.search("Observation", {
        encounter: joinReferenceString("Encounter", encounterId),
        subject: joinReferenceString("Patient", clientId),
        _sort: "-_lastUpdated",
        _count: String(MEDPLUM_OBSERVATION_SEARCH_MAX_COUNT),
      });

      if (seq !== refreshAsyncSeqRef.current) {
        return;
      }

      const observations = result.entry?.map((e) => e.resource as Observation) || [];
      const relatedObservations = relatedPatientId
        ? await fetchRelatedPatientObservations(relatedPatientId)
        : [];

      if (seq !== refreshAsyncSeqRef.current) {
        return;
      }

      const observationsFromHttp = [...observations, ...relatedObservations];
      const reduxEntities = selectPostpartumObservations(store.getState());
      const mergedObservations = mergeIncomingObservationsIntoCache(
        observationsFromHttp,
        reduxEntities,
        observationsCacheRef,
      );

      if (seq !== refreshAsyncSeqRef.current) {
        return;
      }

      const latestMotherCumulativeBloodLossObservation = mergedObservations
        .filter((observation) => isMotherCumulativeBloodLossObservation(observation, clientId))
        .sort((a, b) => observationMetaTimeMs(b) - observationMetaTimeMs(a))[0];
      applyMotherCumulativeBloodLossIfNewer(
        latestMotherCumulativeBloodLossObservation,
        motherCblLastAppliedMetaMsRef,
        setLiveCumulativeBloodLoss,
      );

      if (seq !== refreshAsyncSeqRef.current) {
        return;
      }

      await processObservations(mergedObservations);

      if (seq !== refreshAsyncSeqRef.current) {
        return;
      }
    } catch (error) {
      if (!isOptimistic) {
        Sentry.captureException(error);
        toast({
          type: "error",
          title: "Could not refresh observations",
          message:
            "Live postpartum data may be out of date. Try reloading the encounter or check your connection, then try again.",
          autoClose: false,
        });
      }
    }
  }, [
    medplum,
    encounterId,
    clientId,
    processObservations,
    isOptimistic,
    relatedPatientId,
    fetchRelatedPatientObservations,
    store,
  ]);

  useEffect(() => {
    dispatch(clearPostpartumObservations());
    observationsCacheRef.current = new Map();
  }, [dispatch, encounterId, clientId]);

  useEffect(() => {
    if (skipInitialRefresh) {
      return;
    }
    void refreshObservations();
  }, [encounterId, clientId, birthDateTime, refreshObservations, skipInitialRefresh]);

  useEffect(() => {
    if (!encounterId || !clientId) {
      return;
    }
    if (previousStatusChangedRef.current === statusChanged) {
      return;
    }
    previousStatusChangedRef.current = statusChanged;
    medplum.invalidateSearches("Observation");
    void refreshObservations();
  }, [statusChanged, encounterId, clientId, medplum, refreshObservations]);

  useEffect(() => {
    return () => {
      observationsCacheRef.current = new Map();
    };
  }, []);

  const handleEncounterObservationBundle = useCallback(
    async (bundle: Bundle) => {
      const asyncSeq = ++bundleAsyncSeqRef.current;
      const resources = getResourcesFromBundle("Observation", bundle as Bundle<Observation>);
      await runLaborEncounterObservationBundle(resources, asyncSeq, {
        clientId,
        relatedPatientId: relatedPatientIdRef.current,
        stages: stagesRef.current,
        dispatch,
        getState: () => store.getState(),
        observationsCacheRef,
        motherCblLastAppliedMetaMsRef,
        setLiveCumulativeBloodLoss: (value: number) => setLiveCumulativeBloodLoss(value),
        processObservations: (observations, sequence) => {
          if (sequence !== bundleAsyncSeqRef.current || !processObservationsRef.current) {
            return Promise.resolve();
          }
          return processObservationsRef.current(observations);
        },
        fetchLaborStageObservationsBundle: () =>
          fetchLaborStageObservations(medplum, clientId, encounterId),
        bundleAsyncSeqRef,
      });
    },
    [clientId, dispatch, encounterId, medplum, store],
  );

  const resyncAfterSubscriptionConnect = useCallback(async () => {
    await refreshObservations();
    try {
      const allObservations = await fetchLaborStageObservations(medplum, clientId, encounterId);
      dispatch(
        loadLaborStages(
          dedupeLaborStagesPreservingAmendPreviews(allObservations, stagesRef.current),
        ),
      );
    } catch (error) {
      if (!isOptimistic) {
        Sentry.captureException(error);
        toast({
          type: "error",
          title: "Could not resync labor stages",
          message:
            "Labor stage data may be out of date after reconnecting. Refresh the encounter or the page if stages look wrong.",
          autoClose: false,
        });
      }
    }
  }, [refreshObservations, medplum, clientId, encounterId, dispatch, isOptimistic]);

  useSubscription(
    `Observation?encounter=Encounter/${encounterId}&_sort=-_lastUpdated`,
    (bundle: Bundle) => {
      bundleProcessingChainRef.current = bundleProcessingChainRef.current
        .then(() => handleEncounterObservationBundle(bundle))
        .catch((error) => {
          if (!isOptimistic) {
            Sentry.captureException(error);
            toast({
              type: "error",
              title: "Could not apply live observation update",
              message:
                "An update arrived but the chart may not reflect it yet. Try refreshing the encounter or reloading the page.",
              autoClose: false,
            });
          }
        });
    },
    {
      onError: (error: Error) => {
        if (!isOptimistic) {
          Sentry.captureException(error);
          toast({
            type: "error",
            title: "Labor observation stream interrupted",
            message:
              "Real-time updates paused. Reload the page or navigate away and back to reconnect. Birth and vitals on screen may be stale until you refresh.",
            autoClose: false,
          });
        }
      },
      onSubscriptionConnect: useCallback(() => {
        if (!subscriptionConnectHandledOnceRef.current) {
          subscriptionConnectHandledOnceRef.current = true;
          return;
        }
        void resyncAfterSubscriptionConnect();
      }, [resyncAfterSubscriptionConnect]),
    },
  );

  const contextValue = useMemo(
    () => ({
      encounterId,
      clientId,
      refreshObservations,
      liveCumulativeBloodLoss,
    }),
    [encounterId, clientId, refreshObservations, liveCumulativeBloodLoss],
  );

  return (
    <LaborEncounterLiveObservationsContext.Provider value={contextValue}>
      {children}
    </LaborEncounterLiveObservationsContext.Provider>
  );
}
